import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { listCompanies } from '../api/business/endpoints';
import type { Company } from '../api/business/types';
import { usePermissions } from '../auth/usePermissions';

const STORAGE_KEY = 'stock.bo.company';

const readStored = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * The company a page is about — the products page and the movements page share it, and it survives a
 * reload. Only companies on which the user holds `permission` are offered.
 */
export const useSelectedCompany = (permission: string) => {
  const permissions = usePermissions();
  const query = useQuery({ queryKey: ['companies'], queryFn: listCompanies });
  const [storedId, setStoredId] = useState<string | null>(readStored);

  const companies: Array<Company> = (query.data ?? []).filter((company) =>
    permissions.can(permission, company.id),
  );
  const company: Company | null = companies.find((one) => one.id === storedId) ?? companies[0] ?? null;

  const select = (companyId: string): void => {
    try {
      window.localStorage.setItem(STORAGE_KEY, companyId);
    } catch {
      // The choice simply does not survive a reload.
    }

    setStoredId(companyId);
  };

  return {
    companies,
    company,
    select,
    isLoading: query.isLoading,
    error: query.error,
    retry: () => void query.refetch(),
  };
};
